import { useState, useCallback, useRef } from 'react'
import { clsx } from 'clsx'
import { Upload, FileText, Wand2, Check, AlertCircle, X, ChevronDown } from 'lucide-react'
import { Modal, ModalFooter, Button, Spinner, Alert } from '@orientamos/ui'
import { extractIntakeFromText, type AIExtractionResult } from '@lib/api'
import { read, utils } from 'xlsx'
import mammoth from 'mammoth'
import * as pdfjsLib from 'pdfjs-dist'

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

interface AIImportModalProps {
  open: boolean
  onClose: () => void
  onApply: (fields: AIExtractionResult['fields']) => void
}

type InputMode = 'text' | 'file'
type ProcessingState = 'idle' | 'extracting-text' | 'processing' | 'reviewing' | 'error'

/**
 * Extract text from a PDF file using pdf.js
 */
async function extractTextFromPDF(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise

  const textParts: string[] = []
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const textContent = await page.getTextContent()
    const pageText = textContent.items.map((item) => ('str' in item ? item.str : '')).join(' ')
    textParts.push(pageText)
  }

  return textParts.join('\n\n')
}

/**
 * Extract text from a DOCX file using mammoth
 */
async function extractTextFromDOCX(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer()
  const result = await mammoth.extractRawText({ arrayBuffer })
  return result.value
}

/**
 * Extract text from an Excel file using SheetJS
 */
function extractTextFromExcel(file: File): Promise<string> {
  return file.arrayBuffer().then((arrayBuffer) => {
    const workbook = read(arrayBuffer, { type: 'array' })
    const textParts: string[] = []

    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName]
      if (!sheet) continue
      const csv = utils.sheet_to_csv(sheet)
      if (csv.trim()) {
        textParts.push(`--- ${sheetName} ---\n${csv}`)
      }
    }

    return textParts.join('\n\n')
  })
}

/**
 * Field labels in Spanish for display
 */
const FIELD_LABELS: Record<string, string> = {
  contractType: 'Tipo de Contrato',
  'property.address': 'Direccion del Inmueble',
  'property.city': 'Ciudad del Inmueble',
  'property.stratum': 'Estrato',
  'property.area': 'Area (m2)',
  'property.propertyType': 'Tipo de Inmueble',
  'tenant.firstName': 'Nombres del Arrendatario',
  'tenant.lastName': 'Apellidos del Arrendatario',
  'tenant.idNumber': 'Cedula/NIT',
  'tenant.phone': 'Telefono',
  'tenant.email': 'Correo Electronico',
  'tenant.address': 'Direccion del Arrendatario',
  'tenant.city': 'Ciudad del Arrendatario',
  'owner.firstName': 'Nombres del Propietario',
  'owner.lastName': 'Apellidos del Propietario',
  'owner.idNumber': 'Cedula/NIT del Propietario',
  'owner.phone': 'Telefono del Propietario',
  'owner.email': 'Correo del Propietario',
  'owner.address': 'Direccion del Propietario',
  'owner.city': 'Ciudad del Propietario',
  'terms.monthlyRent': 'Canon Mensual',
  'terms.administrationFee': 'Administracion',
  'terms.startDate': 'Fecha Inicio',
  'terms.endDate': 'Fecha Fin',
  'terms.durationMonths': 'Duracion (meses)',
}

/**
 * Confidence indicator component
 */
function ConfidenceIndicator({ confidence }: { confidence: number | undefined }) {
  if (confidence === undefined) {
    return (
      <span className="flex items-center text-neutral-400" title="No encontrado">
        <X className="h-4 w-4" />
      </span>
    )
  }

  if (confidence >= 0.8) {
    return (
      <span
        className="flex items-center text-success-600"
        title={`Confianza: ${Math.round(confidence * 100)}%`}
      >
        <Check className="h-4 w-4" />
      </span>
    )
  }

  if (confidence >= 0.5) {
    return (
      <span
        className="flex items-center text-warning-500"
        title={`Baja confianza: ${Math.round(confidence * 100)}%`}
      >
        <AlertCircle className="h-4 w-4" />
      </span>
    )
  }

  return (
    <span
      className="flex items-center text-danger-500"
      title={`Muy baja confianza: ${Math.round(confidence * 100)}%`}
    >
      <AlertCircle className="h-4 w-4" />
    </span>
  )
}

/**
 * Collapsible section for field groups
 */
function FieldSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors"
      >
        <span className="font-medium text-neutral-900">{title}</span>
        <ChevronDown
          className={clsx('h-5 w-5 text-neutral-500 transition-transform', isOpen && 'rotate-180')}
        />
      </button>
      {isOpen && <div className="p-4 space-y-3">{children}</div>}
    </div>
  )
}

/**
 * Editable field row
 */
function FieldRow({
  fieldKey,
  value,
  confidence,
  onChange,
}: {
  fieldKey: string
  value: unknown
  confidence: number | undefined
  onChange: (key: string, value: string) => void
}) {
  const label = FIELD_LABELS[fieldKey] || fieldKey
  const hasValue = value !== null && value !== undefined

  return (
    <div className="flex items-center gap-3">
      <ConfidenceIndicator confidence={hasValue ? confidence : undefined} />
      <label className="text-sm text-neutral-600 w-40 flex-shrink-0">{label}</label>
      <input
        type="text"
        value={hasValue ? String(value) : ''}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        className="flex-1 px-3 py-1.5 text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        placeholder="No encontrado"
      />
    </div>
  )
}

export function AIImportModal({ open, onClose, onApply }: AIImportModalProps) {
  const [inputMode, setInputMode] = useState<InputMode>('text')
  const [textInput, setTextInput] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [state, setState] = useState<ProcessingState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [extractionResult, setExtractionResult] = useState<AIExtractionResult | null>(null)
  const [editedFields, setEditedFields] = useState<AIExtractionResult['fields'] | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleClose = useCallback(() => {
    // Reset state on close
    setState('idle')
    setError(null)
    setExtractionResult(null)
    setEditedFields(null)
    setTextInput('')
    setSelectedFile(null)
    onClose()
  }, [onClose])

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
      const validTypes = [
        'application/pdf',
        'text/plain',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ]
      const validExtensions = ['.pdf', '.txt', '.docx', '.doc', '.xlsx', '.xls']
      const hasValidType = validTypes.includes(file.type)
      const hasValidExtension = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))

      if (file.size > MAX_FILE_SIZE) {
        setError('El archivo es demasiado grande. Máximo 10MB.')
        return
      }

      if (hasValidType || hasValidExtension) {
        setSelectedFile(file)
        setError(null)
      } else {
        setError('Tipo de archivo no soportado. Use PDF, DOCX, Excel o TXT.')
      }
    }
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
      if (file.size > MAX_FILE_SIZE) {
        setError('El archivo es demasiado grande. Máximo 10MB.')
        return
      }
      setSelectedFile(file)
      setError(null)
    }
  }, [])

  const handleProcess = async () => {
    setError(null)
    setState('processing')

    try {
      let textContent: string

      if (inputMode === 'text') {
        if (!textInput.trim()) {
          setError('Por favor ingresa texto para procesar')
          setState('idle')
          return
        }
        textContent = textInput
      } else {
        if (!selectedFile) {
          setError('Por favor selecciona un archivo')
          setState('idle')
          return
        }

        setState('extracting-text')

        // Extract text from file based on type
        const fileName = selectedFile.name.toLowerCase()
        const mimeType = selectedFile.type

        if (fileName.endsWith('.txt') || mimeType === 'text/plain') {
          textContent = await selectedFile.text()
        } else if (fileName.endsWith('.pdf') || mimeType === 'application/pdf') {
          try {
            textContent = await extractTextFromPDF(selectedFile)
          } catch (pdfError) {
            console.error('PDF extraction error:', pdfError)
            setError('Error al leer el archivo PDF. Intenta copiar el texto manualmente.')
            setState('idle')
            return
          }
        } else if (
          fileName.endsWith('.docx') ||
          mimeType.includes('wordprocessingml') ||
          fileName.endsWith('.doc')
        ) {
          try {
            textContent = await extractTextFromDOCX(selectedFile)
          } catch (docxError) {
            console.error('DOCX extraction error:', docxError)
            setError('Error al leer el archivo Word. Intenta copiar el texto manualmente.')
            setState('idle')
            return
          }
        } else if (
          fileName.endsWith('.xlsx') ||
          fileName.endsWith('.xls') ||
          mimeType.includes('spreadsheet') ||
          mimeType === 'application/vnd.ms-excel'
        ) {
          try {
            textContent = await extractTextFromExcel(selectedFile)
          } catch (xlsError) {
            console.error('Excel extraction error:', xlsError)
            setError('Error al leer el archivo Excel. Intenta copiar el texto manualmente.')
            setState('idle')
            return
          }
        } else {
          setError('Tipo de archivo no soportado')
          setState('idle')
          return
        }
      }

      if (!textContent || textContent.trim().length < 10) {
        setError('El contenido extraido es muy corto. Por favor verifica el archivo.')
        setState('idle')
        return
      }

      setState('processing')

      // Send to API for AI extraction
      const result = await extractIntakeFromText(textContent)

      if (!result.success || !result.data) {
        setError(result.error || 'Error al procesar el texto')
        setState('error')
        return
      }

      setExtractionResult(result.data)
      setEditedFields(result.data.fields)
      setState('reviewing')
    } catch (err) {
      console.error('Processing error:', err)
      setError('Error inesperado al procesar. Por favor intenta de nuevo.')
      setState('error')
    }
  }

  const handleFieldChange = (key: string, value: string) => {
    if (!editedFields) return

    const keys = key.split('.')
    const newFields = JSON.parse(JSON.stringify(editedFields)) as AIExtractionResult['fields']

    if (keys.length === 1) {
      // Top-level field like contractType
      ;(newFields as unknown as Record<string, unknown>)[key] = value || null
    } else {
      // Nested field like property.address
      const [section, field] = keys as ['property' | 'tenant' | 'owner' | 'terms', string]
      if (!newFields[section]) {
        newFields[section] = {} as (typeof newFields)[typeof section]
      }
      ;(newFields[section] as unknown as Record<string, unknown>)[field] = value || null
    }

    setEditedFields(newFields)
  }

  const handleApply = () => {
    if (editedFields) {
      onApply(editedFields)
      handleClose()
    }
  }

  const renderIdleState = () => (
    <>
      {/* Input mode tabs */}
      <div className="flex border-b border-neutral-200 mb-4">
        <button
          type="button"
          onClick={() => setInputMode('text')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            inputMode === 'text'
              ? 'text-primary-600 border-primary-600'
              : 'text-neutral-500 border-transparent hover:text-neutral-700'
          )}
        >
          <FileText className="h-4 w-4 inline mr-2" />
          Pegar Texto
        </button>
        <button
          type="button"
          onClick={() => setInputMode('file')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            inputMode === 'file'
              ? 'text-primary-600 border-primary-600'
              : 'text-neutral-500 border-transparent hover:text-neutral-700'
          )}
        >
          <Upload className="h-4 w-4 inline mr-2" />
          Subir Archivo
        </button>
      </div>

      {inputMode === 'text' ? (
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Pega el texto del documento del cual deseas extraer la informacion.
          </p>
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Pega aqui el texto del contrato, correo o documento..."
            className="w-full h-48 px-4 py-3 border border-neutral-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Arrastra un archivo o haz clic para seleccionar. Formatos soportados: PDF, DOCX, Excel,
            TXT.
          </p>
          <div
            onDrop={handleFileDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className={clsx(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              selectedFile
                ? 'border-primary-300 bg-primary-50'
                : 'border-neutral-300 hover:border-primary-400 hover:bg-neutral-50'
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.txt,.xlsx,.xls,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileSelect}
              className="hidden"
            />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-primary-600" />
                <div className="text-left">
                  <p className="font-medium text-neutral-900">{selectedFile.name}</p>
                  <p className="text-sm text-neutral-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-neutral-400 mb-3" />
                <p className="text-neutral-600">Arrastra un archivo aqui</p>
                <p className="text-sm text-neutral-500 mt-1">o haz clic para seleccionar</p>
              </>
            )}
          </div>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
    </>
  )

  const renderProcessingState = () => (
    <div className="py-12 text-center">
      <Spinner size="lg" className="mx-auto mb-4" />
      <p className="text-lg font-medium text-neutral-900">
        {state === 'extracting-text'
          ? 'Extrayendo texto del archivo...'
          : 'Analizando documento...'}
      </p>
      <p className="text-sm text-neutral-500 mt-2">Esto puede tomar unos segundos</p>
    </div>
  )

  const renderReviewState = () => {
    if (!extractionResult || !editedFields) return null

    const { confidence } = extractionResult

    return (
      <div className="space-y-4">
        <Alert variant="info">
          Revisa los campos extraidos y edita si es necesario antes de aplicar.
        </Alert>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {/* Contract Type */}
          <FieldSection title="Tipo de Contrato">
            <FieldRow
              fieldKey="contractType"
              value={editedFields.contractType}
              confidence={confidence.contractType}
              onChange={handleFieldChange}
            />
          </FieldSection>

          {/* Property */}
          <FieldSection title="Inmueble">
            <FieldRow
              fieldKey="property.address"
              value={editedFields.property?.address}
              confidence={confidence['property.address']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="property.city"
              value={editedFields.property?.city}
              confidence={confidence['property.city']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="property.stratum"
              value={editedFields.property?.stratum}
              confidence={confidence['property.stratum']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="property.area"
              value={editedFields.property?.area}
              confidence={confidence['property.area']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="property.propertyType"
              value={editedFields.property?.propertyType}
              confidence={confidence['property.propertyType']}
              onChange={handleFieldChange}
            />
          </FieldSection>

          {/* Tenant */}
          <FieldSection title="Arrendatario">
            <FieldRow
              fieldKey="tenant.firstName"
              value={editedFields.tenant?.firstName}
              confidence={confidence['tenant.firstName']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="tenant.lastName"
              value={editedFields.tenant?.lastName}
              confidence={confidence['tenant.lastName']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="tenant.idNumber"
              value={editedFields.tenant?.idNumber}
              confidence={confidence['tenant.idNumber']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="tenant.phone"
              value={editedFields.tenant?.phone}
              confidence={confidence['tenant.phone']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="tenant.email"
              value={editedFields.tenant?.email}
              confidence={confidence['tenant.email']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="tenant.address"
              value={editedFields.tenant?.address}
              confidence={confidence['tenant.address']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="tenant.city"
              value={editedFields.tenant?.city}
              confidence={confidence['tenant.city']}
              onChange={handleFieldChange}
            />
          </FieldSection>

          {/* Owner */}
          <FieldSection title="Propietario">
            <FieldRow
              fieldKey="owner.firstName"
              value={editedFields.owner?.firstName}
              confidence={confidence['owner.firstName']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="owner.lastName"
              value={editedFields.owner?.lastName}
              confidence={confidence['owner.lastName']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="owner.idNumber"
              value={editedFields.owner?.idNumber}
              confidence={confidence['owner.idNumber']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="owner.phone"
              value={editedFields.owner?.phone}
              confidence={confidence['owner.phone']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="owner.email"
              value={editedFields.owner?.email}
              confidence={confidence['owner.email']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="owner.address"
              value={editedFields.owner?.address}
              confidence={confidence['owner.address']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="owner.city"
              value={editedFields.owner?.city}
              confidence={confidence['owner.city']}
              onChange={handleFieldChange}
            />
          </FieldSection>

          {/* Terms */}
          <FieldSection title="Condiciones">
            <FieldRow
              fieldKey="terms.monthlyRent"
              value={editedFields.terms?.monthlyRent}
              confidence={confidence['terms.monthlyRent']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="terms.administrationFee"
              value={editedFields.terms?.administrationFee}
              confidence={confidence['terms.administrationFee']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="terms.startDate"
              value={editedFields.terms?.startDate}
              confidence={confidence['terms.startDate']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="terms.endDate"
              value={editedFields.terms?.endDate}
              confidence={confidence['terms.endDate']}
              onChange={handleFieldChange}
            />
            <FieldRow
              fieldKey="terms.durationMonths"
              value={editedFields.terms?.durationMonths}
              confidence={confidence['terms.durationMonths']}
              onChange={handleFieldChange}
            />
          </FieldSection>
        </div>
      </div>
    )
  }

  const renderErrorState = () => (
    <div className="py-8 text-center">
      <AlertCircle className="h-12 w-12 mx-auto text-danger-500 mb-4" />
      <p className="text-lg font-medium text-neutral-900 mb-2">Error al procesar</p>
      <p className="text-sm text-neutral-600">{error}</p>
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Importar con IA"
      description="Extrae automaticamente la informacion de un documento"
      size="lg"
    >
      {state === 'idle' && renderIdleState()}
      {(state === 'extracting-text' || state === 'processing') && renderProcessingState()}
      {state === 'reviewing' && renderReviewState()}
      {state === 'error' && renderErrorState()}

      <ModalFooter>
        {state === 'idle' && (
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              onClick={handleProcess}
              disabled={inputMode === 'text' ? !textInput.trim() : !selectedFile}
            >
              <Wand2 className="h-4 w-4 mr-2" />
              Extraer Informacion
            </Button>
          </>
        )}

        {state === 'reviewing' && (
          <>
            <Button
              variant="outline"
              onClick={() => {
                setState('idle')
                setExtractionResult(null)
                setEditedFields(null)
              }}
            >
              Volver
            </Button>
            <Button onClick={handleApply}>
              <Check className="h-4 w-4 mr-2" />
              Aplicar al Formulario
            </Button>
          </>
        )}

        {state === 'error' && (
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setState('idle')
                setError(null)
              }}
            >
              Intentar de nuevo
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  )
}
